import { useNavigate } from 'react-router-dom';

import {
	Box,
	Button,
	Divider,
	Group,
	SkeletonRect,
	Stack,
	Tag,
	Text,
} from '@ekhodealer/ekho-common/rev';

import { useGetLeadDetails } from '@common/api/leads/useGetLeadDetails';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { PLACEHOLDER } from '@common/lib/strings';
import { paths } from '@config/paths';

interface ConversationsContactPanelProps {
	/** The ID of the lead whose contact details are displayed, or null if none selected */
	leadId: string | null;
}

interface DetailRowProps {
	/** Display label for the detail row */
	label: string;
	/** Value to render, or null to show a placeholder */
	value: string | null;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
	<Stack itemsSpacing='4'>
		<Text size='footnote' color='secondary'>
			{label}
		</Text>
		<Text size='bodySmall'>{value || PLACEHOLDER}</Text>
	</Stack>
);

const PanelSkeleton = () => (
	<Box background='secondary' rounding='16' isScrollable='y'>
		<Stack itemsSpacing='12' padding={20}>
			<SkeletonRect width='60%' height={20} />
		</Stack>
		{[1, 2].map((_, idx) => (
			<Stack key={idx} itemsSpacing='12' padding={20}>
				<Stack itemsSpacing='16'>
					{['80%', '70%', '50%', '40%'].map((width, i) => (
						<SkeletonRect key={width + i} width={width} height={16} />
					))}
				</Stack>
				<Divider />
				<Stack itemsSpacing='16'>
					{['60%', '90%'].map((width, i) => (
						<SkeletonRect key={width + i} width={width} height={16} />
					))}
				</Stack>
			</Stack>
		))}
	</Box>
);

export const ConversationsContactPanel = ({ leadId }: ConversationsContactPanelProps) => {
	const navigate = useNavigate();
	const servicesProviderId = useCurrentServicesProviderId();
	const isInbound = leadId?.startsWith('inbound:') ?? false;
	const { data: lead, isLoading } = useGetLeadDetails(isInbound ? null : leadId);

	if (!leadId) {
		return null;
	}

	if (isInbound) {
		return (
			<Box background='secondary' rounding='16'>
				<Stack padding={{ y: 80, x: 20 }} itemsAlignX='center' itemsAlignY='center'>
					<Text color='secondary'>No lead available</Text>
				</Stack>
			</Box>
		);
	}

	if (isLoading || !lead) {
		return <PanelSkeleton />;
	}

	const handleViewLeadDetails = (): void => {
		navigate(paths.leadDetails.getHref(servicesProviderId, 'all', leadId, 'overview'));
	};

	return (
		<Box background='secondary' rounding='16' isScrollable='y'>
			<Stack itemsSpacing='16' padding={20}>
				<Text weight='semibold'>{lead.contactInfo.name}</Text>
				<DetailRow label='Phone number' value={lead.contactInfo.phone} />
				<DetailRow label='Email' value={lead.contactInfo.email} />
				<DetailRow label='Pipeline' value={lead.summary.pipeline} />

				<Stack itemsSpacing='4'>
					<Text size='footnote' color='secondary'>
						Lead type
					</Text>
					<Group>
						<Tag indicator variant='pill' accent={1} size='small'>
							{lead.summary.type}
						</Tag>
					</Group>
				</Stack>

				<Divider />

				<DetailRow label='Cart total' value={lead.summary.cartTotal ?? null} />
				<DetailRow label='Recent activity' value={lead.summary.recentActivity ?? null} />

				<Divider />

				<Button variant='secondary' size='small' onPress={handleViewLeadDetails}>
					View lead details
				</Button>
			</Stack>
		</Box>
	);
};
